import { format, parseISO } from "date-fns";
import { CalendarIcon, LoaderIcon, TriangleAlert } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
	minEffectiveDate,
	onlySeenConflicts,
	type RelocationCheck,
	type RelocationRequest,
	relocationRequest,
	sendJson,
	type TimelineEntry,
} from "@/lib/therapist-relocation";
import { cn } from "@/lib/utils";
import { ConflictList } from "./conflict-list";

/**
 * What the dialog makes utama from a chosen day: a saved address of the therapist, an Alamat Lain
 * or a Pernah Utama, which Go copies into a new row.
 */
export type ScheduleTarget = {
	addressId: number;
	city: string;
	address: string;
	postalCode?: string | null;
};

// The preview stays valid while what Go checks is unchanged; the reason is not checked.
const keyOf = ({ reason: _reason, ...checked }: RelocationRequest) =>
	JSON.stringify(checked);

export function ScheduleRelocationDialog({
	target,
	onClose,
	onSaved,
	relocationsPath,
	entries,
}: {
	/** null keeps the dialog closed */
	target: ScheduleTarget | null;
	onClose: () => void;
	/** Reloads what a write changed. */
	onSaved: () => void;
	/** Preview and save post under it. */
	relocationsPath: string;
	entries: TimelineEntry[];
}) {
	const { t } = useTranslation("therapists", {
		keyPrefix: "relocation.schedule",
	});
	const { locale, timezone } = useDateContext();
	const fieldId = useId();

	const [effectiveDate, setEffectiveDate] = useState("");
	const [reason, setReason] = useState("");
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [check, setCheck] = useState<{
		key: string;
		data: RelocationCheck;
	} | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [attempted, setAttempted] = useState(false);
	const [previewing, setPreviewing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const outcomeRef = useRef<HTMLDivElement>(null);

	const minDate = minEffectiveDate(new Date(), timezone, entries);
	const parsed = relocationRequest(
		{
			effectiveDate,
			reason,
			target: { mode: "existing", addressId: target?.addressId ?? null },
		},
		minDate,
	);
	const issueAt = (...path: string[]) =>
		parsed.error?.issues.some(
			(issue) => issue.path.join(".") === path.join("."),
		);
	const dateError = attempted && issueAt("effectiveDate");
	const reasonError = attempted && issueAt("reason");

	const fresh = parsed.success && check?.key === keyOf(parsed.data);
	const conflicts = fresh ? (check?.data.conflicts ?? []) : [];
	const busy = previewing || saving;
	const place = target;

	// Every opening starts from a blank form.
	useEffect(() => {
		if (!target) return;
		setEffectiveDate("");
		setReason("");
		setCheck(null);
		setMessage(null);
		setAttempted(false);
		setConfirming(false);
	}, [target]);

	useEffect(() => {
		if (message || check)
			outcomeRef.current?.scrollIntoView({
				block: "start",
				behavior: "smooth",
			});
	}, [message, check]);

	// The form is disabled while a request runs, so no answer can arrive for stale inputs.
	const preview = async () => {
		setAttempted(true);
		if (!parsed.success) return;
		const request = parsed.data;
		setMessage(null);
		setPreviewing(true);
		try {
			const result = await sendJson(`${relocationsPath}/preview`, request);
			if (result.ok && result.data)
				setCheck({ key: keyOf(request), data: result.data });
			else setMessage(result.error || t("errors.preview_failed"));
		} catch {
			setMessage(t("errors.preview_failed"));
		} finally {
			setPreviewing(false);
		}
	};

	// The save goes without acknowledge_conflicts first; a visit booked since the preview comes back
	// as a 409 for the admin to see before anything is written.
	const save = async () => {
		setAttempted(true);
		if (!parsed.success) return;
		const request = parsed.data;
		setMessage(null);
		setSaving(true);
		try {
			let result = await sendJson(relocationsPath, {
				...request,
				acknowledge_conflicts: false,
			});
			if (
				result.status === 409 &&
				result.data &&
				conflicts.length > 0 &&
				onlySeenConflicts(result.data.conflicts, conflicts)
			) {
				result = await sendJson(relocationsPath, {
					...request,
					acknowledge_conflicts: true,
				});
			}

			if (result.ok) {
				onClose();
				onSaved();
			} else if (result.status === 409 && result.data) {
				setCheck({ key: keyOf(request), data: result.data });
				setMessage(
					t(conflicts.length > 0 ? "errors.new_conflicts" : "errors.conflicts"),
				);
			} else {
				setMessage(result.error || t("errors.save_failed"));
			}
		} catch {
			setMessage(t("errors.save_failed"));
		} finally {
			setSaving(false);
			setConfirming(false);
		}
	};

	const requestSave = () => {
		if (conflicts.length > 0) setConfirming(true);
		else save();
	};

	return (
		<>
			<Dialog
				open={!!target}
				onOpenChange={(next) => {
					if (!next && !busy) onClose();
				}}
			>
				<DialogContent className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-2xl">
					<DialogHeader className="p-4 pb-3 border-b sm:p-6 sm:pb-4">
						<DialogTitle>{t("title")}</DialogTitle>
						<DialogDescription>{t("description")}</DialogDescription>
					</DialogHeader>

					{/* A fieldset cannot be the scroll box: as a flex item Chromium neither clips nor scrolls it. */}
					<div className="min-h-0 p-4 overflow-y-auto sm:p-6">
						<fieldset disabled={busy} className="grid min-w-0 gap-5">
							{place && (
								<div className="grid gap-0.5 p-3 text-sm border bg-muted/40">
									<span className="font-semibold">{place.city}</span>
									<span className="break-words">
										{place.address}
										{place.postalCode && ` · ${place.postalCode}`}
									</span>
								</div>
							)}

							<div className="grid gap-1.5">
								<Label htmlFor={`${fieldId}-date`}>{t("date.label")}</Label>
								<Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
									<PopoverTrigger asChild>
										<Button
											id={`${fieldId}-date`}
											type="button"
											variant="outline"
											aria-invalid={dateError}
											className={cn(
												"justify-between w-full shadow-inner bg-input sm:w-72",
												!effectiveDate && "text-muted-foreground",
											)}
										>
											{effectiveDate
												? format(parseISO(effectiveDate), "PPPP", { locale })
												: t("date.placeholder")}
											<CalendarIcon className="opacity-75 size-4" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0" align="start">
										<Calendar
											mode="single"
											locale={locale}
											selected={
												effectiveDate ? parseISO(effectiveDate) : undefined
											}
											defaultMonth={parseISO(effectiveDate || minDate)}
											// Calendar days are local midnights, so formatting them keeps the day.
											disabled={(date) => format(date, "yyyy-MM-dd") < minDate}
											onSelect={(date) => {
												if (!date) return;
												setEffectiveDate(format(date, "yyyy-MM-dd"));
												setCalendarOpen(false);
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
								<p
									className={cn(
										"text-xs",
										dateError ? "text-destructive" : "text-muted-foreground",
									)}
								>
									{t("date.hint", {
										date: format(parseISO(minDate), "PP", { locale }),
									})}
								</p>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor={`${fieldId}-reason`}>{t("reason.label")}</Label>
								<Textarea
									id={`${fieldId}-reason`}
									value={reason}
									onChange={(event) => setReason(event.target.value)}
									placeholder={t("reason.placeholder")}
									aria-invalid={reasonError}
									className="shadow-inner bg-input"
								/>
								{reasonError && (
									<p role="alert" className="text-xs text-destructive">
										{t("errors.reason")}
									</p>
								)}
							</div>

							{/* The outcome lands below a long form; bring it into view whenever it changes. */}
							<div
								ref={outcomeRef}
								className="grid gap-5 empty:hidden scroll-mt-4"
							>
								{message && (
									<Alert variant="destructive">
										<TriangleAlert className="size-4" />
										<AlertDescription>{message}</AlertDescription>
									</Alert>
								)}

								{check && (
									<section
										aria-live="polite"
										className={cn("grid gap-2", !fresh && "opacity-50")}
									>
										<h3 className="text-sm font-semibold">
											{check.data.conflicts.length > 0
												? t("conflicts.title", {
														count: check.data.conflicts.length,
													})
												: t("conflicts.none")}
										</h3>
										{check.data.conflicts.length > 0 && (
											<ConflictList conflicts={check.data.conflicts} />
										)}
									</section>
								)}
								{check && !fresh && (
									<p className="-mt-3 text-xs text-muted-foreground">
										{t("stale")}
									</p>
								)}
							</div>
						</fieldset>
					</div>

					<DialogFooter className="gap-2 p-4 border-t sm:p-6 sm:pt-4">
						<Button variant="outline" disabled={busy} onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button
							variant="outline"
							disabled={fresh || busy}
							onClick={preview}
						>
							{previewing && <LoaderIcon className="animate-spin" />}
							{t("preview")}
						</Button>
						<Button disabled={!fresh || busy} onClick={requestSave}>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={confirming}
				onOpenChange={(next) => {
					if (!saving) setConfirming(next);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("confirm.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("confirm.description", { count: conflicts.length })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={saving}>
							{t("confirm.back")}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={saving}
							onClick={(event) => {
								// keep the dialog open until the request finishes
								event.preventDefault();
								save();
							}}
						>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("confirm.save")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
